import type { PartialDeep } from 'type-fest';
import type { Filter } from 'contexts/preferences/listing-params/types';
import type { ParsedSearchFilters } from 'utils/natural-language-filter-parser/types';
import type { SmartSearchTagMatch } from './smart-search.types';
import type { ListingMatchType } from './listing-match';

const SMART_SEARCH_LOG_ENDPOINT = '/api/smart-search/queries';

export interface SmartSearchLoggedLocation {
  id: string;
  slug: string;
  label: string;
  urlPath?: string;
}

export interface SmartSearchAppliedLocation {
  slug: string;
  areaName: string;
  source: 'picked' | 'best_match' | 'none';
}

export interface SmartSearchLoggedListing {
  id: string;
  description: string;
  urlPath?: string;
}

export interface SmartSearchQueryLog {
  tenantId: string;
  userId?: string;
  sessionId?: string;
  locale?: string;
  source?: string;
  createdAt?: string;
  originAreaSlug?: string;
  areaName?: string;
  rawQuery: string;
  unparsed?: string | null;
  confidence?: number | null;
  parsedFilters?: ParsedSearchFilters;
  appliedFilters?: PartialDeep<Filter>;
  smartTagsMatched?: SmartSearchTagMatch[];
  smartTagsApplied?: string[];
  smartTagsManual?: string[];
  smartTagsStatus?: string;
  locationsSuggested?: SmartSearchLoggedLocation[];
  appliedLocation?: SmartSearchAppliedLocation | null;
  locationStatus?: string;
  listingsSuggested?: SmartSearchLoggedListing[];
  appliedListing?: SmartSearchLoggedListing | null;
  matchType?: ListingMatchType;
  parseStatus?: string;
  hadError?: boolean;
}

/**
 * Logs a smart-search submit to the analytics endpoint, fire-and-forget.
 */
export function logSmartSearchQuery(payload: SmartSearchQueryLog): void {
  if (typeof window === 'undefined' || !payload.rawQuery || !payload.tenantId) {
    return;
  }

  const body = JSON.stringify(payload);

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(SMART_SEARCH_LOG_ENDPOINT, blob)) {
        return;
      }
    }
  } catch {
    // sendBeacon can throw (e.g. payload too large) — fall through to fetch.
  }

  if (typeof fetch !== 'function') {
    return;
  }

  try {
    void fetch(SMART_SEARCH_LOG_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      // Best-effort: a failed analytics write is never surfaced to the user.
    });
  } catch {
    // Never let an analytics write throw into the submit handler.
  }
}

export default logSmartSearchQuery;
